import { memo, useCallback, useMemo, useState } from "react";

import { BackAndForth } from "../../components/BackAndForth/BackAndForth";
import { ChartUtils } from "../../components/charts/utils";
import { LoadingOverlay } from "../../components/LoadingOverlay/LoadingOverlay";
import { PageTitle } from "../../components/PageTitle";
import { useOrganizationContext } from "../../contexts/organizations/organizationsContext";
import { usePlaygroundModels } from "../../hooks/reactQueryHooks/usePlayground";
import { useUsage } from "../../hooks/reactQueryHooks/useUsage";
import { Badges } from "./components/Badges";
import { UsageByDays } from "./components/UsageByDays/UsageByDays";
import { UsageTableController } from "./components/UsageTable";
import { UsageTiers } from "./components/UsageTiers";

const billingBreadcrumbs = [
  {
    title: "Organization",
    path: "",
    isSeparator: false,
    isActive: true,
  },
  {
    title: "",
    path: "",
    isSeparator: true,
    isActive: false,
  },
];

const UsageWrapper = memo(() => {
  const { currentOrganization } = useOrganizationContext();
  const [billingOffset, setBillingOffset] = useState(0);

  const { data: models } = usePlaygroundModels();
  const { data, isFetching, isError } = useUsage(
    currentOrganization?.id || "",
    billingOffset,
  );

  const {
    usage,
    tokenUsageTiers,
    currentTierId,
    provider,
    range,
    analytics,
    pastBillingPeriods,
  } = data || {};

  const billingPeriod = useMemo(() => {
    if (range) {
      const from = ChartUtils.getXAxisLabel("day", range.fromDate);
      const to = ChartUtils.getXAxisLabel("day", range.toDate);
      return `${from} - ${to}`;
    }
  }, [range]);

  const chartData = useMemo(() => {
    return { tokens: analytics?.tokens, prices: analytics?.prices };
  }, [analytics]);

  const getPrevBillingPeriod = useCallback(() => {
    if (Math.abs(billingOffset) >= Number(pastBillingPeriods)) {
      return;
    }
    setBillingOffset(prev => prev - 1);
  }, [billingOffset, pastBillingPeriods]);

  const getNextBillingPeriod = useCallback(() => {
    if (billingOffset === 0) {
      return;
    }
    setBillingOffset(prev => prev + 1);
  }, [billingOffset]);

  return (
    <>
      <div className="wrapper-header-export-btn-question-history">
        <PageTitle
          breadcrumbs={billingBreadcrumbs}
          breadcrumbTitle="Usage"
          pageTitle="Usage"
        />

        {billingPeriod && (
          <BackAndForth
            goBack={getPrevBillingPeriod}
            goForward={getNextBillingPeriod}
            value={billingPeriod}
            disabledForward={billingOffset === 0}
            disabledBack={Math.abs(billingOffset) >= Number(pastBillingPeriods)}
          />
        )}
      </div>

      {usage && <Badges usageInfo={usage} />}

      <div className="card flex-shrink-1 flex-grow-1">
        <div className="app-container-inner-wrapper">
          {usage && tokenUsageTiers && chartData.tokens && chartData.prices && (
            <>
              {currentTierId && (
                <UsageTiers
                  usageInfo={usage}
                  tokenUsageTiers={tokenUsageTiers}
                  currentTierId={currentTierId}
                  isStripe={provider === "stripe"}
                  models={models}
                />
              )}
              <UsageTableController
                tableData={usage.perModel}
                models={models}
              />
              <UsageByDays
                tokens={chartData.tokens}
                prices={chartData.prices}
              />
            </>
          )}

          {(isFetching || !data) && !isError && <LoadingOverlay />}
        </div>
      </div>
    </>
  );
});

export { UsageWrapper };
